import { postPasswordReset, postPasswordResetConfirmation, putPassword } from 'shared/api/authApi'
import { getPasswordPolicyError } from 'shared/auth/passwordPolicy'
import { getRecaptchaToken } from 'shared/auth/recaptcha'
import { getApiErrorCode, getApiErrorMessage } from 'shared/utils/apiError'
import { t } from 'shared/i18n'

/**
 * パスワードの変更・再設定。api（`/v1/auth/password*`）への薄いラッパーで state を持たない。
 *
 * メールアドレスの変更は資格情報ではなくユーザー更新の一部として api が扱う
 * （`POST .../email_change_codes` + `PATCH .../users/:userId`）ため、ここには置かない。
 */

/**
 * 再設定フローの失敗を画面へ出せる Error に写す。
 *
 * reCAPTCHA の不合格（403 `recaptcha_failed`）・トークン欠落（422 `recaptcha_token_missing`）も
 * api の `detail` をそのまま出す。再送時は新しいトークンを取り直す。
 *
 * 画面は `error.message` をそのまま出すため、axios の英語メッセージ
 * （`Request failed with status code 401`）を出さないよう api の `detail` へ差し替える。
 * パスワードポリシー不適合だけは、入力のヒントになる既存のポリシー文言を優先する。
 */
const toResetError = (error: unknown): Error => {
  if (getApiErrorCode(error) === 'invalid_password') {
    return new Error(getPasswordPolicyError())
  }
  return new Error(getApiErrorMessage(error) ?? t('shared.accountActions.error'))
}

/** パスワード再設定コードを送る。 */
export async function requestPasswordReset (email: string): Promise<void> {
  const recaptchaToken = await getRecaptchaToken()
  try {
    await postPasswordReset({ email, recaptchaToken })
  } catch (error) {
    throw toResetError(error)
  }
}

/** 再設定コードと新パスワードでパスワードを確定する。 */
export async function confirmPasswordReset (email: string, code: string, newPassword: string): Promise<void> {
  const recaptchaToken = await getRecaptchaToken()
  try {
    await postPasswordResetConfirmation({ email, code, newPassword, recaptchaToken })
  } catch (error) {
    throw toResetError(error)
  }
}

/**
 * ログイン中ユーザーのパスワードを変更する。
 * 成功すると api がそのユーザーの全 API セッションを失効する。後始末は呼び出し側（useAuth）が行う。
 */
export async function changeCurrentPassword (oldPassword: string, newPassword: string): Promise<void> {
  await putPassword({ currentPassword: oldPassword, newPassword })
}
